/**
 * Pick default engine resource ids (pricinglists, bucketlists, places).
 */

/**
 * @param {Array<{ _id?: string, is_default?: boolean }>|undefined|null} items
 * @returns {string|null}
 */
export function findDefaultResourceId(items) {
    const list = Array.isArray(items) ? items : [];
    const marked = list.find((i) => i?.is_default === true);
    return marked?._id ?? null;
}

/**
 * Display label for a resource (pricinglist / bucketlist / place).
 *
 * @param {{ _id?: string, name?: string }|undefined|null} item
 * @returns {string}
 */
function resource_display_name(item) {
    if (!item) {
        return '';
    }
    if (item.name != null && String(item.name).trim() !== '') {
        return String(item.name);
    }
    return item._id != null ? String(item._id) : '';
}

/**
 * Resolve which resource Engine would apply when the source id is blank.
 * Aligns with engine importer `resolveListId` blank branch:
 * `is_default` → `_id === "default"` → sole item.
 * Does not use last-used prefs (display / empty select only).
 *
 * @param {Array<{ _id?: string, name?: string, is_default?: boolean }>|undefined|null} items
 * @returns {{ id: string, name: string, source: 'default'|'legacy_id'|'sole' }|null}
 */
export function resolveDisplayDefaultResource(items) {
    const list = Array.isArray(items) ? items : [];
    const marked = list.find((i) => i?.is_default === true);
    if (marked?._id != null && marked._id !== '') {
        return {
            id: String(marked._id),
            name: resource_display_name(marked),
            source: 'default',
        };
    }
    const legacy = list.find((i) => i && String(i._id) === 'default');
    if (legacy) {
        return {
            id: String(legacy._id),
            name: resource_display_name(legacy),
            source: 'legacy_id',
        };
    }
    if (list.length === 1 && list[0]?._id != null && list[0]._id !== '') {
        return {
            id: String(list[0]._id),
            name: resource_display_name(list[0]),
            source: 'sole',
        };
    }
    return null;
}

/**
 * @param {Array<{ _id?: string }>|undefined|null} items
 * @param {string|null|undefined} id
 * @returns {boolean}
 */
function resourceIdIsKnown(items, id) {
    if (id == null || id === '') {
        return false;
    }
    const list = Array.isArray(items) ? items : [];
    return list.some((i) => i && String(i._id) === String(id));
}

/**
 * @param {{ items?: Array<{ _id?: string, is_default?: boolean }>, currentId?: string|null, singleId?: string|null, lastStoredId?: string|null }} args
 * @returns {string|null}
 */
export function pickDefaultResourceId({ items, currentId, singleId, lastStoredId }) {
    if (currentId != null && currentId !== '') {
        return currentId;
    }
    const defaultId = findDefaultResourceId(items);
    if (defaultId) {
        return defaultId;
    }
    const list = Array.isArray(items) ? items : [];
    if (list.length === 1) {
        return list[0]._id ?? null;
    }
    if (singleId != null && singleId !== '' && resourceIdIsKnown(list, singleId)) {
        return singleId;
    }
    if (lastStoredId != null && lastStoredId !== '' && resourceIdIsKnown(list, lastStoredId)) {
        return lastStoredId;
    }
    return null;
}
